import React from "react";

export const Shell = ({ children }) => (
  <div className="caf-theme-wrapper min-h-screen overflow-x-hidden bg-slate-50 p-3 text-slate-900 sm:p-4 md:p-6">
    <div className="mx-auto max-w-7xl space-y-4 sm:space-y-5">{children}</div>
  </div>
);

export const Card = ({ title, subtitle, right, children, className = "" }) => (
  <section className={`min-w-0 rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
    {(title || right) && (
      <header className="border-b border-slate-100 p-3 sm:p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h3 className="text-sm font-bold tracking-tight text-slate-900 sm:text-base">{title}</h3>
            {subtitle && <p className="mt-0.5 text-xs font-medium text-slate-500">{subtitle}</p>}
          </div>
          {right && <div className="min-w-0 max-w-full">{right}</div>}
        </div>
      </header>
    )}
    <div className="min-w-0 overflow-x-auto p-3 sm:p-4">{children}</div>
  </section>
);

const TONES = {
  cyan: "border-sky-200 bg-sky-50 text-sky-700",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  rose: "border-rose-200 bg-rose-50 text-rose-700",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-700"
};

const VALUE_COLORS = {
  cyan: "text-sky-900",
  emerald: "text-emerald-900",
  amber: "text-amber-900",
  rose: "text-rose-900",
  indigo: "text-indigo-900"
};

export const Kpi = ({ label, value, hint, tone = "cyan", icon }) => (
  <div className={`rounded-2xl border ${TONES[tone]} min-w-0 p-3 shadow-sm transition-all hover:shadow-md sm:p-5`}>
    <div className="flex items-center justify-between gap-1 text-[10px] font-bold uppercase tracking-wider opacity-80 sm:text-xs sm:tracking-widest">
      <span className="truncate text-slate-600">{label}</span><span className="text-lg sm:text-xl">{icon}</span>
    </div>
    <div className={`mt-1 truncate text-2xl font-black sm:mt-2 sm:text-3xl ${VALUE_COLORS[tone]}`}>{value}</div>
    {hint && <div className="mt-1 truncate text-[11px] font-semibold text-slate-500 sm:text-xs">{hint}</div>}
  </div>
);

export const Tabs = ({ value, onChange, options }) => (
  <div className="no-scrollbar inline-flex max-w-full overflow-x-auto whitespace-nowrap rounded-xl border border-slate-200 bg-slate-100 p-1">
    {options.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`min-h-[40px] shrink-0 rounded-lg px-4 py-1.5 text-xs font-bold transition-all sm:min-h-0 ${value === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
        {label}
      </button>
    ))}
  </div>
);

export const Empty = ({ children = "No data yet for this period." }) => (
  <div className="flex h-40 flex-col items-center justify-center space-y-2 text-sm font-medium text-slate-400">
    <div className="text-2xl opacity-50">🔍</div>
    <div>{children}</div>
  </div>
);

export const Loading = () => (
  <div className="flex h-40 flex-col items-center justify-center space-y-3">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-sky-500" />
    <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Loading Intelligence...</div>
  </div>
);

export const ErrorBox = ({ message }) => message ? (
  <div className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700 shadow-sm">
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-[10px] text-white font-black">!</span>
    <div className="flex-1">{message}</div>
    <button onClick={() => window.location.reload()} className="text-xs underline hover:text-rose-800">Retry</button>
  </div>
) : null;

export const selectCls = "rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 shadow-sm transition-all";